import { Box, Button, Container, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import useUrlQuery from "../../hooks/useUrlQuery";
import {
  useGetQuote,
  useGetViewPlanDetails,
} from "../../services/Quotes.service";
import BackButton from "../../components/BackButton/BackButton";
import { Navigate, useNavigate } from "react-router-dom";

const Compare = () => {
  const { selected_plan_for_compare_product_ids } = useTypedSelector(
    (state) => state.quote
  );
  const { getUrlQuery } = useUrlQuery();
  const quote_id = getUrlQuery("quote_id");
  const navigate = useNavigate();
  const plans_to_map = [...selected_plan_for_compare_product_ids];

  if (!selected_plan_for_compare_product_ids.length)
    return (
      <Navigate
        to={{ pathname: "/quote", search: `quote_id=${quote_id}` }}
        replace
      />
    );

  return (
    <>
      <Container
        sx={{
          py: "20px",
          px: { xs: 1, sm: 3 }
        }}
      >
        <BackButton
          label="Quotes"
          onClick={() => {
            navigate({
              pathname: "/quote",
              search: `quote_id=${quote_id}`,
            });
          }}
        />
        <Typography
          mb={3}
          variant="h6"
          color="primary"
          align="center"
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
        >
          Find the best plan that suits your needs
        </Typography>

        {/* Mobile Simple View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <SimpleMobileView selected_plan_for_compare_product_ids={selected_plan_for_compare_product_ids} />
        </Box>

        {/* Desktop Grid View */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <DesktopGridView selected_plan_for_compare_product_ids={selected_plan_for_compare_product_ids} />
        </Box>
      </Container>
    </>
  );
};

export default Compare;

const SimpleMobileView = ({ selected_plan_for_compare_product_ids }) => {
  const { getUrlQuery } = useUrlQuery();
  const quote_id = getUrlQuery("quote_id");

  // Get plan details for each plan using hooks at component level
  const plan1 = selected_plan_for_compare_product_ids[0];
  const plan2 = selected_plan_for_compare_product_ids[1];
  const plan3 = selected_plan_for_compare_product_ids[2];

  const endpoint1 = plan1 ? `/api/GetHealthQuotes/${plan1?.slug}/${plan1?.product_id}/${quote_id}/${plan1?.cover_amount}` : "";
  const endpoint2 = plan2 ? `/api/GetHealthQuotes/${plan2?.slug}/${plan2?.product_id}/${quote_id}/${plan2?.cover_amount}` : "";
  const endpoint3 = plan3 ? `/api/GetHealthQuotes/${plan3?.slug}/${plan3?.product_id}/${quote_id}/${plan3?.cover_amount}` : "";

  const { premium_data: premium1, product_data: product1 } = useGetQuote(endpoint1);
  const { premium_data: premium2, product_data: product2 } = useGetQuote(endpoint2);
  const { premium_data: premium3, product_data: product3 } = useGetQuote(endpoint3);

  const { viewPlanDetails: viewPlan1 } = useGetViewPlanDetails(plan1?.product_id);
  const { viewPlanDetails: viewPlan2 } = useGetViewPlanDetails(plan2?.product_id);
  const { viewPlanDetails: viewPlan3 } = useGetViewPlanDetails(plan3?.product_id);

  // Create planDetails array
  const planDetails = [
    plan1 && { premium_data: premium1, product_data: product1, viewPlanDetails: viewPlan1, plan: plan1 },
    plan2 && { premium_data: premium2, product_data: product2, viewPlanDetails: viewPlan2, plan: plan2 },
    plan3 && { premium_data: premium3, product_data: product3, viewPlanDetails: viewPlan3, plan: plan3 }
  ].filter(Boolean);

  return (
    <Box sx={{ pb: 2 }}>
      {/* Plan Header Table */}
      <TableContainer
        component={Paper}
        sx={{
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: 'text.secondary',
                  border: 'none',
                  py: 1.5
                }}
              >
                Plans
              </TableCell>
              {planDetails.map((plan, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: 'text.secondary',
                    border: 'none',
                    py: 1.5,
                    px: 0.5
                  }}
                >
                  Plan {index + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Logo Row */}
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'text.primary',
                  borderBottom: '1px solid #f0f0f0',
                  py: 1
                }}
              >
                Insurance Name
              </TableCell>
              {planDetails.map((plan, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    py: 1,
                    px: 0.5
                  }}
                >
                  <Box
                    component="img"
                    src={plan.product_data?.LOGO_PATH}
                    alt={plan.product_data?.INSURANCE_NAME}
                    sx={{
                      width: '60px',
                      height: '30px',
                      objectFit: 'contain'
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>

            {/* Plan Name Row */}
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'text.primary',
                  borderBottom: '1px solid #f0f0f0',
                  py: 1
                }}
              >
                Plan Name
              </TableCell>
              {planDetails.map((plan, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    py: 1,
                    px: 0.5
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'text.primary',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      wordBreak: 'break-word'
                    }}
                  >
                    {plan.product_data?.PRODUCT_NAME}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>

            {/* Premium Row */}
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'text.primary',
                  borderBottom: '1px solid #f0f0f0',
                  py: 1
                }}
              >
                Premium
              </TableCell>
              {planDetails.map((plan, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    py: 1,
                    px: 0.5
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'primary.main',
                      fontWeight: 600
                    }}
                  >
                    ₹{Number(plan.premium_data?.TOTAL_PREMIUM || 0).toLocaleString("en-IN")}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Features Comparison Tables */}
      {planDetails[0]?.viewPlanDetails?.data && Object.values(planDetails[0].viewPlanDetails.data).map((section, sectionIndex) => (
        <TableContainer
          key={sectionIndex}
          component={Paper}
          sx={{
            mb: 2,
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          {/* Section Header */}
          <Box
            sx={{
              background: 'f8f9fa',
              color: 'white',
              p: 1.5,
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px'
              }}
            >
              {section.category}
            </Typography>
          </Box>

          <Table size="small">
            <TableBody>
              {section?.features?.map((feature, featureIndex) => (
                <TableRow key={featureIndex}>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      fontSize: '13px',
                      color: 'text.primary',
                      borderBottom: '1px solid #f0f0f0',
                      py: 1,
                      width: '35%',
                      lineHeight: 1.2
                    }}
                  >
                    {feature.FEATURE_TITLE}
                  </TableCell>
                  {planDetails.map((plan, planIndex) => (
                    <TableCell
                      key={planIndex}
                      align="center"
                      sx={{
                        borderBottom: '1px solid #f0f0f0',
                        py: 1,
                        px: 0.5,
                        width: `${65 / planDetails.length}%`
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: 'text.primary',
                          fontWeight: 400,
                          lineHeight: 1.2,
                          wordBreak: 'break-word'
                        }}
                      >
                        {plan.viewPlanDetails?.data?.[sectionIndex]?.features?.[featureIndex]?.FEATURE_VAL || 'N/A'}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </Box>
  );
};

// Keep the existing DesktopGridView, ProductCard, and FeaturesSection components unchanged
const DesktopGridView = ({ selected_plan_for_compare_product_ids }) => {
  const plans_to_map = [...selected_plan_for_compare_product_ids];

  return (
    <>
      {/* Plan Cards Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12 / (plans_to_map.length + 1)}>
            <Typography fontSize={"18px"} color="text.secondary">
              Plan Details
            </Typography>
          </Grid>
          {selected_plan_for_compare_product_ids.map((item, index) => (
            <Grid key={index} item xs={12 / (plans_to_map.length + 1)}>
              <ProductCard selected_plan={item} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <FeaturesSection
        selected_plan_for_compare_product_ids={selected_plan_for_compare_product_ids}
      />
    </>
  );
};

const ProductCard = ({ selected_plan }) => {
  const { getUrlQuery } = useUrlQuery();
  const quote_id = getUrlQuery("quote_id");
  const endpoint = selected_plan
    ? `/api/GetHealthQuotes/${selected_plan?.slug}/${selected_plan?.product_id}/${quote_id}/${selected_plan?.cover_amount}`
    : "";
  const { premium_data, product_data } = useGetQuote(endpoint);

  return (
    <Box
      width={"100%"}
      display={"flex"}
      justifyContent={"center"}
      flexDirection={"column"}
      border={"1px solid #ccc"}
      borderRadius={"8px"}
      padding={"16px"}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        gap="12px"
        sx={{
          "& >img": {
            maxWidth: "120px",
            height: "60px",
          },
        }}
      >
        <img
          width={"120px"}
          height={"auto"}
          src={product_data?.LOGO_PATH}
          alt={product_data?.INSURANCE_NAME}
        />
        <Box>
          <Typography fontSize="15px" color="text.secondary">
            {product_data?.PRODUCT_NAME}
          </Typography>
        </Box>
      </Box>
      <Button
        sx={{
          mt: 2,
        }}
        variant="contained"
      >
        ₹ {Number(premium_data?.TOTAL_PREMIUM || 0).toLocaleString("en-IN")}
      </Button>
    </Box>
  );
};

const FeaturesSection = ({ selected_plan_for_compare_product_ids }) => {
  const { viewPlanDetails } = useGetViewPlanDetails(
    selected_plan_for_compare_product_ids[0]?.product_id
  );
  const { viewPlanDetails: viewPlanDetails2 } = useGetViewPlanDetails(
    selected_plan_for_compare_product_ids?.[1]?.product_id
  );
  const { viewPlanDetails: viewPlanDetails3 } = useGetViewPlanDetails(
    selected_plan_for_compare_product_ids?.[2]?.product_id
  );
  const allPlanDetails = [viewPlanDetails, viewPlanDetails2, viewPlanDetails3];

  return (
    <>
      {Object.values(viewPlanDetails?.data || {}).map((item, section_index) => (
        <Paper
          key={section_index}
          sx={{
            p: "12px",
            mt: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12 / (selected_plan_for_compare_product_ids.length + 1)}
            >
              <Typography fontSize={"20px"} color="primary">
                {item.category}
              </Typography>
            </Grid>
            <Grid
              item
              xs={
                12 - 12 / (selected_plan_for_compare_product_ids.length + 1)
              }
            ></Grid>
            {item?.features?.map((feature, feature_index) => (
              <>
                <Grid
                  key={`title-${feature_index}`}
                  item
                  xs={12 / (selected_plan_for_compare_product_ids.length + 1)}
                >
                  <Typography fontSize={"18px"} color="text.secondary">
                    {feature.FEATURE_TITLE}
                  </Typography>
                </Grid>
                {selected_plan_for_compare_product_ids.map((_, index) => (
                  <Grid
                    key={`value-${index}`}
                    item
                    xs={
                      12 /
                      (selected_plan_for_compare_product_ids.length + 1)
                    }
                  >
                    {
                      allPlanDetails[index]?.data?.[section_index]
                        ?.features?.[feature_index]?.FEATURE_VAL
                    }
                  </Grid>
                ))}
              </>
            ))}
          </Grid>
        </Paper>
      ))}
    </>
  );
};